import type { TiptapDocNode } from '@/shared/types/resource';

export interface DocumentImagePlaceholderSummary {
  id: string | null;
  imageQuery: string;
}

export function readTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function buildPlaceholderKey(id: string | null, pos: number): string {
  return id ? `id:${id}` : `pos:${pos}`;
}

export function extractImagePlaceholders(
  doc: TiptapDocNode | null | undefined
): DocumentImagePlaceholderSummary[] {
  if (!doc || typeof doc !== 'object') return [];

  const placeholders: DocumentImagePlaceholderSummary[] = [];

  const visit = (node: TiptapDocNode | null | undefined) => {
    if (!node || typeof node !== 'object') return;

    if (node.type === 'imagePlaceholder') {
      placeholders.push({
        id: readTrimmedString(node.attrs?.id) || null,
        imageQuery: readTrimmedString(node.attrs?.imageQuery),
      });
    }

    if (Array.isArray(node.content)) {
      node.content.forEach(visit);
    }
  };

  visit(doc);

  return placeholders;
}

export function countImagePlaceholders(doc: TiptapDocNode | null | undefined): number {
  return extractImagePlaceholders(doc).length;
}

/**
 * Walk up from `pos` to check whether it sits inside a table cell or header.
 * Works with both ProseMirror `Node` (live editor) and serialised TipTap docs.
 */
export function isPositionInsideTableCell(
  doc: {
    resolve: (pos: number) => {
      depth: number;
      node: (depth: number) => { type: { name: string } };
    };
  },
  pos: number
): boolean {
  try {
    const $pos = doc.resolve(pos);
    for (let depth = $pos.depth; depth > 0; depth -= 1) {
      const nodeName = $pos.node(depth).type.name;
      if (nodeName === 'tableCell' || nodeName === 'tableHeader') return true;
    }
  } catch {
    return false;
  }
  return false;
}
